/**
 * Board-driven multi-step planning targeting. The player selects modes in the
 * inline targeting bar and taps unit/card/cell targets directly on the board.
 * These pure helpers reuse engine enumeration (`enumeratePlanningActions` →
 * complete, validated actions), so the UI only offers prefixes of legal actions
 * and commits one intact.
 */
import {
  implicitPlanningSteps,
  targetCandidateKey,
  type Action,
  type CardDefinition,
  type CellId,
  type GameState,
  type PublicTargetContext,
  type TargetCandidate,
  type TargetCardinality,
  type TargetStepSelection,
} from '@cards/engine';

export type PlanCardAction = Extract<Action, { kind: 'PlanCard' }>;

/** Live board-targeting session: the committed step prefix plus the in-progress current step. */
export interface TargetingSession {
  readonly instanceId: string;
  readonly previous: readonly TargetStepSelection[];
  readonly current: readonly TargetCandidate[];
}

export interface BoardStepView {
  readonly stepId: string;
  readonly cardinality: TargetCardinality;
  readonly ordered: boolean;
  readonly stepIndex: number;
  readonly totalSteps: number;
  readonly isLastStep: boolean;
  /** Valid *next* picks for the current step (already-picked cells excluded), keyed by board cell. */
  readonly candidateCells: ReadonlyMap<CellId, TargetCandidate>;
  /** Valid mode picks for the current step. */
  readonly candidateModes: readonly Extract<TargetCandidate, { kind: 'mode' }>[];
  /** Cells of the current in-progress selection. */
  readonly selectedCells: ReadonlyMap<CellId, TargetCandidate>;
  /** Whether `current` is a committable selection for this step. */
  readonly canConfirm: boolean;
}

/** The single board cell a candidate occupies, or undefined for off-board targets. */
export function candidateCellId(state: GameState, candidate: TargetCandidate): CellId | undefined {
  if (candidate.kind === 'cell') {
    return state.board.cells[candidate.cellId] === undefined ? undefined : candidate.cellId;
  }
  if (candidate.kind === 'unit' || candidate.kind === 'card') {
    const cellId = state.instances[candidate.instanceId]?.cellId;
    return cellId !== undefined && state.board.cells[cellId] !== undefined ? cellId : undefined;
  }
  return undefined;
}

function keysOf(selection: readonly TargetCandidate[]): Set<string> {
  return new Set(selection.map(targetCandidateKey));
}

function sameSet(left: readonly TargetCandidate[], right: readonly TargetCandidate[]): boolean {
  if (left.length !== right.length) return false;
  const leftKeys = keysOf(left);
  return right.every((candidate) => leftKeys.has(targetCandidateKey(candidate)));
}

/** An action extends `previous` when each committed step matches by id and (unordered) selection set. */
function hasPrefix(action: PlanCardAction, previous: readonly TargetStepSelection[]): boolean {
  const selections = action.selections ?? [];
  return previous.every((step, index) => {
    const actionStep = selections[index];
    return actionStep?.stepId === step.stepId && sameSet(actionStep.selection, step.selection);
  });
}

/**
 * True when every legal PlanCard can be expressed by inline modes plus on-board
 * taps and no step is an ordered multi-select. Single-cell/overlay cards are
 * handled by direct drops; table targets use the direct location path.
 */
export function isBoardTargetable(
  definition: CardDefinition,
  context: PublicTargetContext,
  state: GameState,
  actions: readonly PlanCardAction[],
): boolean {
  if (actions.length === 0) return false;
  let hasBoardCandidate = false;
  for (const action of actions) {
    for (const step of action.selections ?? []) {
      for (const candidate of step.selection) {
        if (candidateCellId(state, candidate) !== undefined) hasBoardCandidate = true;
        else if (candidate.kind !== 'mode') return false;
      }
    }
  }
  const steps = implicitPlanningSteps(definition, context, []);
  // Targetless cards (e.g. `target: 'none'` reactions) have nothing to tap on
  // the board — they stage through the wizard's "confirm without choice" path.
  if (steps.length === 0) return false;
  for (const step of steps) {
    // A skippable (min 0) or ordered multi-select step can't be expressed by taps.
    if (step.spec.cardinality.min < 1) return false;
    if ((step.spec.ordered ?? false) && step.spec.cardinality.max > 1) return false;
  }
  return hasBoardCandidate;
}

/** Projects the current step of a session into board-cell candidates, selection, and confirmability. */
export function computeStepView(
  definition: CardDefinition,
  context: PublicTargetContext,
  state: GameState,
  actions: readonly PlanCardAction[],
  previous: readonly TargetStepSelection[],
  current: readonly TargetCandidate[],
): BoardStepView | undefined {
  const steps = implicitPlanningSteps(definition, context, previous);
  const stepIndex = previous.length;
  const step = steps[stepIndex];
  if (step === undefined) return undefined;

  const compatible = actions.filter((action) => hasPrefix(action, previous));
  const currentKeys = keysOf(current);
  const stepSelectionOf = (action: PlanCardAction): readonly TargetCandidate[] | undefined => {
    const selection = action.selections?.[stepIndex];
    return selection?.stepId === step.stepId ? selection.selection : undefined;
  };
  const isSupersetOfCurrent = (selection: readonly TargetCandidate[]): boolean => {
    const keys = keysOf(selection);
    for (const key of currentKeys) if (!keys.has(key)) return false;
    return true;
  };

  const candidateCells = new Map<CellId, TargetCandidate>();
  const candidateModes = new Map<string, Extract<TargetCandidate, { kind: 'mode' }>>();
  if (current.length < step.spec.cardinality.max) {
    for (const action of compatible) {
      const selection = stepSelectionOf(action);
      if (selection === undefined || !isSupersetOfCurrent(selection)) continue;
      for (const candidate of selection) {
        if (currentKeys.has(targetCandidateKey(candidate))) continue;
        const cell = candidateCellId(state, candidate);
        if (cell !== undefined && !candidateCells.has(cell)) candidateCells.set(cell, candidate);
        else if (candidate.kind === 'mode' && !candidateModes.has(candidate.id)) {
          candidateModes.set(candidate.id, candidate);
        }
      }
    }
  }

  const selectedCells = new Map<CellId, TargetCandidate>();
  for (const candidate of current) {
    const cell = candidateCellId(state, candidate);
    if (cell !== undefined) selectedCells.set(cell, candidate);
  }

  const canConfirm = current.length >= step.spec.cardinality.min
    && current.length <= step.spec.cardinality.max
    && compatible.some((action) => {
      const selection = stepSelectionOf(action);
      return selection !== undefined && sameSet(selection, current);
    });

  return {
    stepId: step.stepId,
    cardinality: step.spec.cardinality,
    ordered: step.spec.ordered ?? false,
    stepIndex,
    totalSteps: steps.length,
    isLastStep: stepIndex === steps.length - 1,
    candidateCells,
    candidateModes: [...candidateModes.values()],
    selectedCells,
    canConfirm,
  };
}

/** The complete, validated action whose selections exactly equal `previous`, if any. */
export function commitCompleteAction(
  actions: readonly PlanCardAction[],
  previous: readonly TargetStepSelection[],
): PlanCardAction | undefined {
  return actions.find((action) =>
    hasPrefix(action, previous) && (action.selections?.length ?? 0) === previous.length);
}
